import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DecisionAssistantApiService } from './decision-assistant-api.service';
import {
  DECISION_ASSISTANT_MAX_TURNS,
  DECISION_ASSISTANT_QUESTION_MAX_LENGTH,
  type DecisionAssistantConfig,
  type DecisionAssistantTurn,
} from './decision-assistant.model';
import {
  SAFE_LINK_LIMIT,
  SafeLinkTextComponent,
  safeLinkTokens,
  type SafeLinkToken,
} from './safe-link-text.component';

type AnswerBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'ordered-list'; start: number; items: readonly string[] }
  | { kind: 'unordered-list'; items: readonly string[] };

type LinkedAnswerBlock =
  | { kind: 'paragraph'; tokens: readonly SafeLinkToken[] }
  | {
      kind: 'ordered-list';
      start: number;
      items: readonly { tokens: readonly SafeLinkToken[] }[];
    }
  | { kind: 'unordered-list'; items: readonly { tokens: readonly SafeLinkToken[] }[] };

@Component({
  selector: 'app-decision-assistant',
  standalone: true,
  imports: [ReactiveFormsModule, SafeLinkTextComponent],
  templateUrl: './decision-assistant.component.html',
  styleUrl: './decision-assistant.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecisionAssistantComponent {
  private static readonly MAX_ANSWER_BLOCKS = 16;
  private static readonly MAX_LIST_ITEMS = 24;
  private readonly api = inject(DecisionAssistantApiService);
  readonly config = input<DecisionAssistantConfig | null>(null);
  readonly ownerTurns = input<readonly DecisionAssistantTurn[]>([]);
  readonly maxQuestionLength = DECISION_ASSISTANT_QUESTION_MAX_LENGTH;
  readonly question = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(DECISION_ASSISTANT_QUESTION_MAX_LENGTH)],
  });
  readonly modelRoute = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  });
  readonly asking = signal(false);
  readonly error = signal<string | null>(null);
  private readonly localTurns = signal<DecisionAssistantTurn[]>([]);
  private configuredPresentationKey = '';
  private presentationGeneration = 0;
  private pendingIntent: {
    fingerprint: string;
    turnId: string;
    idempotencyKey: string;
  } | null = null;

  readonly turns = computed(() => {
    const config = this.config();
    if (!config) return [];
    const permittedRoutes = new Set(config.models.map((model) => model.route));
    const matchingOwner = this.ownerTurns().filter(
      (turn) =>
        permittedRoutes.has(turn.modelRoute) &&
        turn.contentRevision === config.expectedContentRevision &&
        turn.responseRevision === config.expectedResponseRevision &&
        turn.contractHash === config.expectedContractHash &&
        turn.presentationRevision === config.expectedPresentationRevision &&
        turn.presentationHash === config.expectedPresentationHash,
    );
    const ownerIds = new Set(matchingOwner.map((turn) => turn.turnId));
    return [
      ...matchingOwner,
      ...this.localTurns().filter((turn) => !ownerIds.has(turn.turnId)),
    ].slice(-DECISION_ASSISTANT_MAX_TURNS);
  });

  constructor() {
    effect(() => {
      const config = this.config();
      if (!config) return;
      const presentationKey = `${config.assignmentId}:${config.expectedContentRevision}:${config.expectedResponseRevision}:${config.expectedContractHash}:${config.expectedPresentationRevision}:${config.expectedPresentationHash}`;
      if (presentationKey === this.configuredPresentationKey) return;
      this.configuredPresentationKey = presentationKey;
      this.presentationGeneration++;
      this.modelRoute.setValue(config.defaultModelRoute, { emitEvent: false });
      this.modelRoute.enable({ emitEvent: false });
      this.question.setValue('', { emitEvent: false });
      this.question.enable({ emitEvent: false });
      this.localTurns.set([]);
      this.error.set(null);
      this.asking.set(false);
      this.pendingIntent = null;
    });
    this.question.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.error.set(null));
    this.modelRoute.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.error.set(null));
  }

  modelLabel(route: string): string {
    return this.config()?.models.find((model) => model.route === route)?.label ?? 'AI';
  }

  answerBlocks(answer: string | null): readonly AnswerBlock[] {
    const paragraphGroups = (answer ?? '')
      .replace(/\r\n?/g, '\n')
      .split(/\n[\t ]*\n/)
      .filter((block) => block.trim().length > 0);
    const blocks = paragraphGroups.flatMap((group) => this.parseAnswerGroup(group));
    if (blocks.length <= DecisionAssistantComponent.MAX_ANSWER_BLOCKS) return blocks;
    const lastBlockIndex = DecisionAssistantComponent.MAX_ANSWER_BLOCKS - 1;
    return [
      ...blocks.slice(0, lastBlockIndex),
      {
        kind: 'paragraph' as const,
        text: blocks.slice(lastBlockIndex).map(this.answerBlockText).join('\n\n'),
      },
    ];
  }

  answerDisplayBlocks(answer: string | null): readonly LinkedAnswerBlock[] {
    let remainingLinks = SAFE_LINK_LIMIT;
    const linkify = (text: string) => {
      const tokens = safeLinkTokens(text, remainingLinks);
      remainingLinks -= tokens.filter((token) => token.kind === 'link').length;
      return { tokens };
    };
    return this.answerBlocks(answer).map((block) => {
      if (block.kind === 'paragraph') return { kind: block.kind, ...linkify(block.text) };
      return {
        kind: block.kind,
        ...(block.kind === 'ordered-list' ? { start: block.start } : {}),
        items: block.items.map(linkify),
      } as LinkedAnswerBlock;
    });
  }

  private parseAnswerGroup(group: string): AnswerBlock[] {
    const blocks: AnswerBlock[] = [];
    let paragraphLines: string[] = [];
    let listKind: 'ordered-list' | 'unordered-list' | null = null;
    let listStart = 1;
    let listNumbers: number[] = [];
    let listItems: string[] = [];
    const flushParagraph = () => {
      if (paragraphLines.length === 0) return;
      blocks.push({ kind: 'paragraph', text: paragraphLines.join('\n') });
      paragraphLines = [];
    };
    const flushList = () => {
      if (!listKind) return;
      const orderedSequenceIsValid =
        listKind !== 'ordered-list' ||
        listNumbers.every((number, index) => number === listStart + index);
      if (listItems.length > DecisionAssistantComponent.MAX_LIST_ITEMS || !orderedSequenceIsValid) {
        blocks.push({
          kind: 'paragraph',
          text: listItems
            .map((item, index) =>
              listKind === 'ordered-list' ? `${listNumbers[index]}. ${item}` : `- ${item}`,
            )
            .join('\n'),
        });
      } else if (listKind === 'ordered-list') {
        blocks.push({ kind: listKind, start: listStart, items: listItems });
      } else {
        blocks.push({ kind: listKind, items: listItems });
      }
      listKind = null;
      listNumbers = [];
      listItems = [];
    };

    for (const line of group.split('\n')) {
      const ordered = line.match(/^\s*(\d{1,3})\.\s+(.+)$/);
      const unordered = line.match(/^\s*[-*•]\s+(.+)$/);
      const nextListKind = ordered ? 'ordered-list' : unordered ? 'unordered-list' : null;
      if (!nextListKind) {
        flushList();
        paragraphLines.push(line);
        continue;
      }
      flushParagraph();
      if (listKind && listKind !== nextListKind) flushList();
      if (!listKind) {
        listKind = nextListKind;
        if (ordered) listStart = Number(ordered[1]);
      }
      if (ordered) {
        listNumbers.push(Number(ordered[1]));
        listItems.push(ordered[2]);
      } else if (unordered) {
        listItems.push(unordered[1]);
      }
    }
    flushList();
    flushParagraph();
    return blocks;
  }

  private readonly answerBlockText = (block: AnswerBlock): string => {
    if (block.kind === 'paragraph') return block.text;
    return block.items
      .map((item, index) =>
        block.kind === 'ordered-list' ? `${block.start + index}. ${item}` : `- ${item}`,
      )
      .join('\n');
  };

  async ask(): Promise<void> {
    const config = this.config();
    const question = this.question.value.trim();
    const modelRoute = this.modelRoute.value;
    if (!config || this.asking()) return;
    if (!question || question.length > DECISION_ASSISTANT_QUESTION_MAX_LENGTH) {
      this.question.markAsTouched();
      this.error.set(
        question
          ? `Keep the question within ${DECISION_ASSISTANT_QUESTION_MAX_LENGTH} characters.`
          : 'Write a question before asking.',
      );
      return;
    }
    if (!config.models.some((model) => model.route === modelRoute)) {
      this.error.set('Choose one of the available AI models.');
      return;
    }
    const fingerprint = JSON.stringify([
      config.assignmentId,
      config.expectedContentRevision,
      config.expectedResponseRevision,
      config.expectedContractHash,
      config.expectedPresentationRevision,
      config.expectedPresentationHash,
      modelRoute,
      question,
    ]);
    if (this.pendingIntent?.fingerprint !== fingerprint)
      this.pendingIntent = {
        fingerprint,
        turnId: crypto.randomUUID(),
        idempotencyKey: crypto.randomUUID(),
      };
    const generation = this.presentationGeneration;
    this.asking.set(true);
    this.modelRoute.disable({ emitEvent: false });
    this.question.disable({ emitEvent: false });
    this.error.set(null);
    try {
      const response = await this.api.ask({
        assignmentId: config.assignmentId,
        expectedContentRevision: config.expectedContentRevision,
        expectedResponseRevision: config.expectedResponseRevision,
        expectedContractHash: config.expectedContractHash,
        expectedPresentationRevision: config.expectedPresentationRevision,
        expectedPresentationHash: config.expectedPresentationHash,
        modelRoute,
        question,
        turnId: this.pendingIntent.turnId,
        idempotencyKey: this.pendingIntent.idempotencyKey,
      });
      if (generation !== this.presentationGeneration) return;
      this.localTurns.update((turns) => [
        ...turns.filter((turn) => turn.turnId !== response.turnId),
        {
          turnId: response.turnId,
          question,
          answer: null,
          modelRoute,
          status: 'pending',
          externalEffects: 'none',
          contentRevision: config.expectedContentRevision,
          responseRevision: config.expectedResponseRevision,
          contractHash: config.expectedContractHash,
          presentationRevision: config.expectedPresentationRevision,
          presentationHash: config.expectedPresentationHash,
        },
      ]);
      this.pendingIntent = null;
      this.question.setValue('', { emitEvent: false });
    } catch (error) {
      if (generation !== this.presentationGeneration) return;
      this.error.set(
        error instanceof Error
          ? error.message
          : 'The AI assistant could not accept this question. Try again.',
      );
    } finally {
      if (generation === this.presentationGeneration) {
        this.asking.set(false);
        this.modelRoute.enable({ emitEvent: false });
        this.question.enable({ emitEvent: false });
      }
    }
  }
}
